import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, AlertTriangle, X } from 'lucide-react';
import { Report, ServiceTransaction } from '../../types';
import { SifaTeilnahme, SifaUnterweisung, SifaUnterweisungVorlage } from '../../services/sifa/types';
import {
  FassungBereitsAktuellError, aktualisiereAusVorlage, createUnterweisung, createUnterweisungAusVorlage, deleteUnterweisungEntwurf,
  markiereGebucht, ordneTeilnahmeZu, setzeLink, signedUrlZertifikat,
} from '../../services/sifa/sifaService';
import { alsVorlageSpeichern, fetchVorlage, fetchVorlagen, istSifaFachkraft } from '../../services/sifa/vorlagenService';
import { fassungVeraltet } from '../../services/sifa/vorlagenLogic';
import { rechneUnterweisungsBuchung, waehleUnterweisungsPositionen } from '../../services/sifa/unterweisungBooking';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { JournalMirrorRequest, queueJournalMirror } from '../../services/serviceJournal';
import { BillingSelectionModal } from '../BillingSelectionModal';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useRechte } from '../../hooks/useRechte';
import { UnterweisungListe } from './UnterweisungListe';
import { UnterweisungEditor } from './UnterweisungEditor';
import { TeilnahmenListe } from './TeilnahmenListe';
import { AnlegeWeg, NeueUnterweisungDialog } from './NeueUnterweisungDialog';
import { SammelLinkKarte } from './SammelLinkKarte';
import { useUnterweisungen } from './useUnterweisungen';
import { heuteIso, istAbrechenbar, leererEntwurf, markiereLokalGebucht } from './unterweisungModulLogic';
import { alsVorlageMeldung, darfAlsVorlageSpeichern, titelOderVorgabe } from './neueUnterweisungLogic';
import { loeschMeldung } from './unterweisungAblageLogic';

interface Props {
  project: { id: string; name?: string; data: Report };
  userProfile: any;
  onBack: () => void;
  /** Schreibt die verbuchte Leistung ins Projekt. Ohne ihn wird nicht gebucht. */
  onUpdate?: (report: Report) => void;
  isReadOnly?: boolean;
}

type Ansicht =
  | { art: 'liste' }
  | { art: 'editor'; id: string }
  | { art: 'teilnahmen'; id: string | null };

/**
 * Unterweisungen eines SiFa-Projekts — duenner Container.
 *
 * Er laedt Kurse, Teilnahmen und Mitarbeiterstamm, schaltet zwischen Ablage,
 * Editor und Teilnahmenliste und haelt den einen Vorgang, der Geld bewegt:
 * die Abrechnung. Jede Regel dazu steht in unterweisungModulLogic.ts bzw.
 * services/sifa/unterweisungBooking.ts (kein jsdom — Komponenten sind nicht
 * testbar, Logik ist es).
 *
 * ═══ DER RIEGEL GEGEN DIE DOPPELBUCHUNG ═══════════════════════════════════
 * Gebucht wird je Teilnahme GENAU EINMAL. Der harte Riegel sitzt in der
 * Datenbank: `markiereGebucht` (RPC sifa_teilnahme_gebucht_markieren)
 * schreibt nur Zeilen mit `gebucht_am is null` und meldet, WELCHE es
 * waren. Deshalb laeuft er ZUERST, vor der Gutschrift ins Projekt — und
 * gutgeschrieben werden genau die gemeldeten ids, keine andere.
 * Ein Lesen-dann-Schreiben haette genau das Fenster, das eine Doppelbuchung
 * braucht (zwei Innendienstler, dieselbe Liste, derselbe Knopf).
 */
export const UnterweisungModul: React.FC<Props> = ({ project, userProfile, onBack, onUpdate, isReadOnly = false }) => {
  const isMobile = useIsMobile();
  const meta = project.data?.metadata;
  const {
    unterweisungen: geladeneUnterweisungen, teilnahmen, mitarbeiter, laden, fehler,
    neuLaden, uebernehmen, uebernehmeTeilnahme, setzeTeilnahmen,
  } = useUnterweisungen(project.id);

  /** Hier geloeschte Entwuerfe — ohne Neuladen aus der Ablage genommen (der Hook kennt kein Entfernen). */
  const [geloeschtIds, setGeloeschtIds] = useState<ReadonlySet<string>>(() => new Set());
  const unterweisungen = useMemo(
    () => (geloeschtIds.size === 0 ? geladeneUnterweisungen : geladeneUnterweisungen.filter((u) => !geloeschtIds.has(u.id))),
    [geladeneUnterweisungen, geloeschtIds],
  );

  const [ansicht, setAnsicht] = useState<Ansicht>({ art: 'liste' });
  const [anlegenLaeuft, setAnlegenLaeuft] = useState(false);
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null);
  const [zertifikatBusyId, setZertifikatBusyId] = useState<string | null>(null);
  const [buchungIds, setBuchungIds] = useState<string[] | null>(null);
  const [buchungLaeuft, setBuchungLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  /** Vorlagen der Firma nach id — nur fuer den Abgleich „Neue Fassung" in der Liste. */
  const [vorlagen, setVorlagen] = useState<ReadonlyMap<string, SifaUnterweisungVorlage>>(() => new Map());
  const [istSifa, setIstSifa] = useState(false);
  const [uebernehmenBusyId, setUebernehmenBusyId] = useState<string | null>(null);
  const [alsVorlageBusyId, setAlsVorlageBusyId] = useState<string | null>(null);
  const [loeschenBusyId, setLoeschenBusyId] = useState<string | null>(null);

  /** Riegel gegen den zweiten Fingertipp — Zustand steht erst nach dem naechsten Zeichnen. */
  const anlegenRef = useRef(false);
  const buchungRef = useRef(false);
  const uebernehmenRef = useRef(false);
  const alsVorlageRef = useRef(false);
  const loeschenRef = useRef(false);

  const istAdmin = userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  // Rollensystem: „SiFa-Vorlagen" gibt „Als Vorlage speichern" frei (ODER
  // neben dem Haekchen), „Preise sehen" die Preise im Abrechnungsdialog.
  // Admins haben jedes Recht.
  const rechte = useRechte(userProfile?.role);
  const nutzerId: string | undefined = typeof userProfile?.id === 'string' ? userProfile.id : undefined;

  // Einmal je Modul: der Abgleich ist eine Zugabe. Scheitert er (Nr. 141
  // fehlt, kein Leserecht), steht nur „aus Vorlage" da, nie „neue Fassung".
  useEffect(() => {
    let aktiv = true;
    fetchVorlagen()
      .then((liste) => { if (aktiv) setVorlagen(new Map(liste.map((v) => [v.id, v]))); })
      .catch(() => { /* still: ohne Abgleich keine Marke „neue Fassung" */ });
    return () => { aktiv = false; };
  }, []);

  // Admins duerfen ohnehin — die Abfrage braucht es nur fuer alle anderen.
  useEffect(() => {
    if (istAdmin) return;
    let aktiv = true;
    istSifaFachkraft(nutzerId)
      .then((ja) => { if (aktiv) setIstSifa(ja); })
      .catch(() => { /* fail-open: kein Haekchen */ });
    return () => { aktiv = false; };
  }, [istAdmin, nutzerId]);

  const darfAlsVorlage = darfAlsVorlageSpeichern(userProfile?.role, istSifa, rechte.hat('sifa.vorlagen'));

  const offene = ansicht.art === 'editor' ? unterweisungen.find((u) => u.id === ansicht.id) || null : null;

  /** Das ganze LV des Projekts (bei Toechtern bucht das Modul hier bewusst nicht auf die Mutter). */
  const alleServices = useMemo(() => meta?.globalData?.services || [], [meta]);
  const billingServices = useMemo(() => waehleUnterweisungsPositionen(alleServices), [alleServices]);
  const kannAbrechnen = !isReadOnly && !!onUpdate && billingServices.length > 0;

  const zeigeFehler = (text: string) => { setMeldungTon('fehler'); setMeldung(text); };
  const zeigeInfo = (text: string) => { setMeldungTon('info'); setMeldung(text); };

  // ─── Anlegen ──────────────────────────────────────────────────────────────

  const neueUnterweisung = () => {
    if (isReadOnly) { zeigeFehler('Nur-Ansicht: In diesem Projekt wird keine Unterweisung angelegt.'); return; }
    if (anlegenRef.current) return;
    setMeldung(null);
    setAnlegenOffen(true);
  };

  /**
   * Legt nach der Wahl im Dialog an — leer oder als Kopie einer Vorlage.
   * Ein Fehler schliesst den Dialog und steht in der Meldungszeile, wie
   * vorher: beim naechsten Versuch laedt der Dialog die Vorlagen frisch.
   */
  const anlegen = async (weg: AnlegeWeg) => {
    if (isReadOnly) { setAnlegenOffen(false); zeigeFehler('Nur-Ansicht: In diesem Projekt wird keine Unterweisung angelegt.'); return; }
    if (anlegenRef.current) return;
    anlegenRef.current = true;
    setAnlegenLaeuft(true);
    setMeldung(null);
    try {
      const angelegt = weg.art === 'vorlage'
        ? await createUnterweisungAusVorlage(project.id, weg.vorlage)
        : await createUnterweisung(project.id, {
          ...leererEntwurf(),
          titel: titelOderVorgabe(weg.titel),
          sprache: 'de',
        });
      // Die gewaehlte Vorlage kann neuer sein als der Abgleich beim Oeffnen.
      if (weg.art === 'vorlage') setVorlagen((prev) => new Map(prev).set(weg.vorlage.id, weg.vorlage));
      uebernehmen(angelegt);
      setAnlegenOffen(false);
      setAnsicht({ art: 'editor', id: angelegt.id });
    } catch (e) {
      setAnlegenOffen(false);
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      anlegenRef.current = false;
      setAnlegenLaeuft(false);
    }
  };

  // ─── Vorlagen ─────────────────────────────────────────────────────────────

  /**
   * Uebernimmt die neue Fassung (Rueckfrage lief in der Liste). Die Vorlage
   * wird vorher frisch gelesen: der Abgleich stammt vom Oeffnen des Moduls,
   * inzwischen kann sie weiter, archiviert oder schon uebernommen sein.
   */
  const neueFassungUebernehmen = async (u: SifaUnterweisung, v: SifaUnterweisungVorlage) => {
    if (isReadOnly || u.status === 'archiviert') return;
    if (uebernehmenRef.current) return;
    uebernehmenRef.current = true;
    setUebernehmenBusyId(u.id);
    setMeldung(null);
    try {
      const frisch = await fetchVorlage(v.id);
      setVorlagen((prev) => {
        const neu = new Map(prev);
        if (frisch) neu.set(frisch.id, frisch); else neu.delete(v.id);
        return neu;
      });
      if (!frisch) throw new Error('Die Vorlage ist nicht mehr verfügbar.');
      if (frisch.status !== 'aktiv') throw new Error('Die Vorlage ist nicht mehr freigegeben — es gibt keine neue Fassung zu übernehmen.');
      if (!fassungVeraltet(u, frisch)) {
        zeigeInfo(`Nr. ${u.nummer} ist bereits auf Fassung ${frisch.fassung} der Vorlage.`);
        return;
      }
      const neu = await aktualisiereAusVorlage(u, frisch);
      uebernehmen(neu);
      zeigeInfo(`Nr. ${neu.nummer}: Fassung ${frisch.fassung} der Vorlage übernommen. Link, Nummer und bisherige Teilnahmen sind geblieben.`);
    } catch (e) {
      // Ein zweiter Browser war schneller (oder die Liste war alt): kein
      // Fehler — der frisch gelesene Stand ersetzt die Zeile in der Liste.
      if (e instanceof FassungBereitsAktuellError) {
        uebernehmen(e.stand);
        zeigeInfo(`Nr. ${e.stand.nummer}: ${e.message}`);
      } else {
        zeigeFehler(rechtFehlerOder(e));
      }
    } finally {
      uebernehmenRef.current = false;
      setUebernehmenBusyId(null);
    }
  };

  const alsVorlage = async (u: SifaUnterweisung) => {
    if (!darfAlsVorlage) return;
    if (alsVorlageRef.current) return;
    alsVorlageRef.current = true;
    setAlsVorlageBusyId(u.id);
    setMeldung(null);
    try {
      const { vorlage, hinweise } = await alsVorlageSpeichern(u);
      zeigeInfo(alsVorlageMeldung(vorlage.titel, hinweise));
    } catch (e) {
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      alsVorlageRef.current = false;
      setAlsVorlageBusyId(null);
    }
  };

  // ─── Entwurf loeschen ─────────────────────────────────────────────────────

  /**
   * Loescht einen Entwurf (Rueckfrage lief in der Liste). Ob es Teilnahmen
   * gibt, prueft der Service vorher und die Delete-Policy (Nr. 143) hart —
   * eine Absage kommt als Klartext („bitte archivieren").
   */
  const entwurfLoeschen = async (u: SifaUnterweisung) => {
    if (isReadOnly || u.status !== 'entwurf') return;
    if (loeschenRef.current) return;
    loeschenRef.current = true;
    setLoeschenBusyId(u.id);
    setMeldung(null);
    try {
      const gab = await deleteUnterweisungEntwurf(u);
      setGeloeschtIds((prev) => new Set(prev).add(u.id));
      zeigeInfo(loeschMeldung(u, gab));
    } catch (e) {
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      loeschenRef.current = false;
      setLoeschenBusyId(null);
    }
  };

  // ─── Link ─────────────────────────────────────────────────────────────────

  const linkSchalten = async (u: SifaUnterweisung, aktiv: boolean) => {
    if (isReadOnly) return;
    setLinkBusyId(u.id);
    setMeldung(null);
    try {
      const neu = await setzeLink(u.id, aktiv);
      uebernehmen(neu);
      zeigeInfo(aktiv ? 'Der Kundenlink ist aktiv — kopieren oder als QR-Code ausgeben.' : 'Der Kundenlink ist deaktiviert; laufende Durchführungen werden nicht mehr angenommen.');
    } catch (e) {
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      setLinkBusyId(null);
    }
  };

  // ─── Teilnahmen ───────────────────────────────────────────────────────────

  const zertifikatOeffnen = async (t: SifaTeilnahme) => {
    if (!t.zertifikatPfad) return;
    setZertifikatBusyId(t.id);
    setMeldung(null);
    try {
      const url = await signedUrlZertifikat(t.zertifikatPfad);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      setZertifikatBusyId(null);
    }
  };

  const zuordnen = async (t: SifaTeilnahme, mitarbeiterId: string) => {
    if (isReadOnly) throw new Error('Nur-Ansicht: keine Zuordnung möglich.');
    const neu = await ordneTeilnahmeZu(t.id, mitarbeiterId);
    uebernehmeTeilnahme(neu);
  };

  // ─── Abrechnung ───────────────────────────────────────────────────────────

  const starteBuchung = (ids: string[]) => {
    if (!kannAbrechnen || ids.length === 0) return;
    setBuchungIds(ids);
  };

  /**
   * Die Buchung — je KURS ein Rechenlauf, auch wenn die Auswahl aus „Alle
   * Teilnahmen" mehrere Kurse mischt: bookingReference, Beschreibung und
   * (bei einer Stundenposition) die Kursdauer haengen am Kurs. Die
   * Leistungsliste wird dabei von Kurs zu Kurs durchgereicht, jeder Lauf
   * haengt seinen eigenen Protokolleintrag an.
   *
   * Gutgeschrieben wird NUR, was die Datenbank als gebucht zurueckmeldet
   * (markiereGebucht liefert die tatsaechlich markierten ids) — nie „die
   * ersten n". Eine leere Antwort heisst: alles war schon gebucht, es gibt
   * nichts gutzuschreiben.
   */
  const buchungBestaetigt = async (serviceId: string | null) => {
    const ids = buchungIds;
    setBuchungIds(null);
    if (!ids || !serviceId) return;                 // „ohne Leistungsbuchung"
    if (isReadOnly || !onUpdate) return;            // zweite Reihe zum Riegel oben
    if (buchungRef.current) return;                 // zweiter Fingertipp

    buchungRef.current = true;
    setBuchungLaeuft(true);
    zeigeInfo('Leistung wird gebucht…');
    // Die Zweitschrift ins Journal wird nur VORGEMERKT und erst hinter dem try
    // gestellt: gespeichert wird zentral nach dem bestaetigten Flush, und ein
    // Stolpern der Zweitschrift darf nie wie ein Fehlschlag der Buchung aussehen.
    let vormerkung: JournalMirrorRequest | null = null;
    try {
      const gewaehlt = teilnahmen.filter((t) => ids.includes(t.id) && istAbrechenbar(t));
      const performerName = userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || undefined : undefined;
      const datum = heuteIso();
      const kontextFuer = (unterweisungId: string) => {
        const kurs = unterweisungen.find((u) => u.id === unterweisungId);
        return {
          performerName,
          datum,
          unterweisungTitel: kurs?.titel || 'Unterweisung',
          unterweisungNummer: kurs?.nummer || 0,
          dauerMin: kurs?.dauerMin ?? null,
        };
      };
      const jeKurs = new Map<string, SifaTeilnahme[]>();
      for (const t of gewaehlt) (jeKurs.get(t.unterweisungId) ?? jeKurs.set(t.unterweisungId, []).get(t.unterweisungId)!).push(t);

      // 0) Rechnen ist rein und schreibt nichts — es sagt nur, welche
      //    Teilnahmen ueberhaupt buchbar sind und welche Menge herauskommt.
      const geplant: string[] = [];
      let mengeJeTeilnahme = 1;
      for (const [uid, liste] of jeKurs) {
        const probe = rechneUnterweisungsBuchung(alleServices, liste, serviceId, kontextFuer(uid));
        if (!probe) continue;
        geplant.push(...probe.teilnahmeIds);
        // gebuchte_menge je Zeile: Stueck = 1; Stunden = Anteil der Kursmenge.
        mengeJeTeilnahme = probe.teilnahmeIds.length > 0 ? probe.menge / probe.teilnahmeIds.length : 1;
      }
      if (geplant.length === 0) {
        zeigeFehler('Nichts zu buchen: die LV-Position wurde nicht gefunden oder alle gewählten Teilnahmen sind schon gebucht.');
        return;
      }

      // 1) DER RIEGEL ZUERST. Die RPC schreibt nur Zeilen mit gebucht_am NULL
      //    und meldet, WELCHE das waren. Leer = nichts geschrieben, nichts gutschreiben.
      const gebuchtIds = await markiereGebucht(geplant, serviceId, mengeJeTeilnahme);
      const gebuchtAm = new Date().toISOString();
      if (gebuchtIds.length === 0) {
        zeigeInfo('Diese Teilnahmen waren bereits abgerechnet — es wurde nichts doppelt gebucht.');
        void neuLaden();
        return;
      }

      // 2) Gutschrift NUR fuer die gemeldeten ids — je Kurs ein Lauf, die
      //    Leistungsliste wandert von Lauf zu Lauf weiter.
      const gebuchtSet = new Set(gebuchtIds);
      let services = alleServices;
      const logEntries: ServiceTransaction[] = [];
      const teile: string[] = [];
      for (const [uid, liste] of jeKurs) {
        const rest = liste.filter((t) => gebuchtSet.has(t.id));
        if (rest.length === 0) continue;
        const buchung = rechneUnterweisungsBuchung(services, rest, serviceId, kontextFuer(uid));
        if (!buchung) continue;
        services = buchung.services;
        logEntries.push(buchung.logEntry);
        teile.push(`${buchung.teilnahmeIds.length} × Nr. ${kontextFuer(uid).unterweisungNummer} = ${buchung.menge} ${buchung.stundenposition ? 'Std.' : 'Stück'}`);
      }
      if (logEntries.length === 0) {
        zeigeFehler(`Die Datenbank hat ${gebuchtIds.length} Teilnahme(n) als gebucht vermerkt, die Gutschrift ins Projekt konnte nicht berechnet werden. Bitte den Leistungsstand prüfen.`);
        void neuLaden();
        return;
      }

      // 3) Erst jetzt ins Projekt.
      const vorherigerLog = meta?.globalData?.serviceLog || [];
      onUpdate({
        ...project.data,
        metadata: {
          ...meta,
          globalData: {
            ...(meta?.globalData || { status: 'active' as any }),
            services,
            serviceLog: [...vorherigerLog, ...logEntries],
          },
        },
      });

      const projektId = (project.id || '').trim();
      if (projektId && projektId !== 'temp-id') {
        const statements: JournalMirrorRequest['statements'] = {};
        // Eine Unterweisung ist weder Zusatzleistung noch Nachweis einer BEGEHUNG.
        for (const e of logEntries) statements[e.id] = { isAddon: false, coversInspection: false };
        vormerkung = {
          projectId: projektId,
          entries: logEntries,
          previousLog: vorherigerLog,
          source: 'unterweisung',
          linkedProjectConfig: meta?.globalData?.linkedProjectConfig ?? null,
          statements,
        };
      } else {
        console.warn('[UnterweisungModul] Buchung ohne belastbare Projekt-id — nicht im Journal vorgemerkt.');
      }

      setzeTeilnahmen((prev) => markiereLokalGebucht(prev, gebuchtIds, serviceId, gebuchtAm, mengeJeTeilnahme));
      const uebrig = geplant.length - gebuchtIds.length;
      zeigeInfo(
        `${gebuchtIds.length} Teilnahme(n) gebucht: ${teile.join(', ')}.` +
        (uebrig > 0 ? ` ${uebrig} waren bereits abgerechnet und wurden nicht doppelt gebucht.` : ''),
      );
      if (uebrig > 0) void neuLaden();
    } catch (e) {
      zeigeFehler(rechtFehlerOder(e));
    } finally {
      buchungRef.current = false;
      setBuchungLaeuft(false);
    }
    if (vormerkung) queueJournalMirror(vormerkung);
  };

  // ─── Ansicht ──────────────────────────────────────────────────────────────

  if (offene) {
    return (
      <UnterweisungEditor
        unterweisung={offene}
        projectId={project.id}
        isReadOnly={isReadOnly}
        isMobile={isMobile}
        onZurueck={() => setAnsicht({ art: 'liste' })}
        onGespeichert={uebernehmen}
      />
    );
  }

  const teilnahmenKurs = ansicht.art === 'teilnahmen' ? unterweisungen.find((u) => u.id === ansicht.id) || null : null;

  return (
    <div data-glass-view="sifa" className="flex flex-col h-full bg-slate-50">
      <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={ansicht.art === 'liste' ? onBack : () => setAnsicht({ art: 'liste' })} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-slate-800 truncate">
            {ansicht.art === 'teilnahmen' ? (teilnahmenKurs ? `Teilnahmen — Nr. ${teilnahmenKurs.nummer} ${teilnahmenKurs.titel}` : 'Alle Teilnahmen') : 'Unterweisungen'}
          </h1>
          <p className="text-xs text-slate-500 truncate">{project.name || meta?.projectName}</p>
        </div>
        {ansicht.art === 'liste' && teilnahmen.length > 0 && (
          <button onClick={() => setAnsicht({ art: 'teilnahmen', id: null })} className="min-h-[44px] px-3 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl">
            Alle Teilnahmen
          </button>
        )}
      </div>

      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-20' : ''}`}>
        {isReadOnly && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
            Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet. Kurse und Teilnahmen lassen sich ansehen —
            anlegen, ändern, Link schalten, zuordnen und buchen ist gesperrt.
          </div>
        )}

        {meldung && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldungTon === 'fehler' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            {meldungTon === 'fehler' && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
            <span className="flex-1 whitespace-pre-line">{meldung}</span>
            <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {ansicht.art === 'teilnahmen' ? (
          <TeilnahmenListe
            teilnahmen={teilnahmen}
            unterweisungen={unterweisungen}
            mitarbeiter={mitarbeiter}
            unterweisungId={ansicht.id}
            isReadOnly={isReadOnly}
            isMobile={isMobile}
            zertifikatBusyId={zertifikatBusyId}
            buchungLaeuft={buchungLaeuft}
            kannAbrechnen={kannAbrechnen}
            buero={{
              name: (userProfile?.company_name || '').trim(),
              adresse: userProfile?.company_address || null,
              logoDataUrl: userProfile?.company_logo || null,
            }}
            kundeName={(project.name || meta?.projectName || '').trim()}
            onNeuLaden={neuLaden}
            onZertifikat={(t) => void zertifikatOeffnen(t)}
            onZuordnen={zuordnen}
            onAbrechnen={starteBuchung}
          />
        ) : (
          <>
            {/* key: ein anderes Projekt bekommt eine frische Karte, nie den Stand des vorigen. */}
            <SammelLinkKarte
              key={project.id}
              projectId={project.id}
              unterweisungen={unterweisungen}
              unterweisungenLaden={laden}
              unterweisungenFehler={!!fehler}
              isReadOnly={isReadOnly}
              bueroName={(userProfile?.company_name || '').trim()}
              kundeName={(project.name || meta?.projectName || '').trim()}
            />
            <UnterweisungListe
              unterweisungen={unterweisungen}
              teilnahmen={teilnahmen}
              laden={laden}
              fehler={fehler}
              isReadOnly={isReadOnly}
              isMobile={isMobile}
              busy={anlegenLaeuft}
              linkBusyId={linkBusyId}
              onNeu={neueUnterweisung}
              onBearbeiten={(u) => setAnsicht({ art: 'editor', id: u.id })}
              onTeilnahmen={(u) => setAnsicht({ art: 'teilnahmen', id: u.id })}
              onLinkSchalten={(u, aktiv) => void linkSchalten(u, aktiv)}
              bueroName={(userProfile?.company_name || '').trim()}
              kundeName={(project.name || meta?.projectName || '').trim()}
              vorlagen={vorlagen}
              darfAlsVorlageSpeichern={darfAlsVorlage}
              uebernehmenBusyId={uebernehmenBusyId}
              alsVorlageBusyId={alsVorlageBusyId}
              onNeueFassungUebernehmen={(u, v) => void neueFassungUebernehmen(u, v)}
              onAlsVorlageSpeichern={(u) => void alsVorlage(u)}
              loeschenBusyId={loeschenBusyId}
              onEntwurfLoeschen={(u) => void entwurfLoeschen(u)}
            />
          </>
        )}
      </div>

      {anlegenOffen && (
        <NeueUnterweisungDialog
          unterweisungen={unterweisungen}
          laeuft={anlegenLaeuft}
          isReadOnly={isReadOnly}
          onClose={() => setAnlegenOffen(false)}
          onAnlegen={(weg) => void anlegen(weg)}
        />
      )}

      {buchungIds && (
        <BillingSelectionModal
          isOpen
          onClose={() => setBuchungIds(null)}
          onConfirm={(serviceId) => void buchungBestaetigt(serviceId)}
          services={billingServices}
          title={buchungIds.length === 1 ? 'Eine Teilnahme abrechnen' : `${buchungIds.length} Teilnahmen abrechnen`}
          context="unterweisung"
          memoryScope={project.id}
          canSeeFinancials={rechte.hat('preise.sehen')}
        />
      )}
    </div>
  );
};
